import axios, { AxiosError, AxiosInstance } from "axios";
import { config } from "../config/env.config";
import {
  StartInterviewRequest,
  StartInterviewResponse,
  SendMessageRequest,
  SendMessageResponse,
  InterviewSession,
  InterviewConfig,
  InterviewLocation,
  EndInterviewResponse,
  ResumeSessionResponse,
  ContinueResumeResponse,
  RestartResumeResponse,
  SaveProgressResponse,
  SessionMessage,
  PreviousQuestionResponse,
  EditPreviousResponseRequest,
  GenerateBenchmarkCandidateResponseRequest,
  GenerateBenchmarkCandidateResponseResponse,
  BenchmarkOpenRouterUsage,
  SaveBenchmarkEvaluationRequest,
  SaveBenchmarkEvaluationResponse,
  SaveBenchmarkResponseAnnotationRequest,
  BenchmarkResponseAnnotation,
  GetBenchmarkResponseAnnotationsResponse,
  CancelBenchmarkSessionsRequest,
  CancelBenchmarkSessionsResponse,
} from "../types";

interface FileUploadUrlResponse {
  uploadUrl: string;
  fields: Record<string, string>;
  fileUrl: string;
  expiresAt: string;
}

export class ApiService {
  private client: AxiosInstance;

  /**
   * DEVOPS2-670: the TalentHub staff token belongs to the operator, not to a
   * session, so it is shared by every instance. Each benchmark lane builds its
   * own ApiService to hold its own session token, and those instances would
   * otherwise call the benchmark routes unauthenticated (401 on deployed
   * environments; local dev hides it because the guard falls back to origin).
   * Sharing also keeps every lane current when TalentHub re-posts the token.
   * Only benchmarkGet/benchmarkPost ever attach it.
   */
  private static benchmarkAccessToken: string | null = null;

  /**
   * SC-127: session-scoped access token, minted server-side by start/resume.
   * Replaces the old static shared secret that used to be baked into the public
   * JS bundle. Held only in memory — the candidate flow always re-establishes a
   * token via start (fresh) or resume continue/restart (reload) before any
   * guarded call, so there is no need to persist it.
   */
  private sessionToken: string | null = null;

  constructor() {
    this.client = axios.create({
      baseURL: config.apiUrl,
      headers: {
        "Content-Type": "application/json",
      },
    });

    // Attach the session token (once we have one) to every request. Bootstrap
    // calls (location/config/start/resume lookup) run before a token exists and
    // are public on the backend.
    this.client.interceptors.request.use((requestConfig) => {
      if (this.sessionToken && !requestConfig.headers.Authorization) {
        requestConfig.headers.Authorization = `Bearer ${this.sessionToken}`;
      }
      return requestConfig;
    });
  }

  setSessionToken(token: string): void {
    this.sessionToken = token;
  }

  setBenchmarkAccessToken(token: string | null): void {
    ApiService.benchmarkAccessToken = token;
  }

  private post<T>(url: string, data?: unknown, signal?: AbortSignal) {
    return signal
      ? this.client.post<T>(url, data, { signal })
      : this.client.post<T>(url, data);
  }

  private get<T>(url: string, signal?: AbortSignal) {
    return signal
      ? this.client.get<T>(url, { signal })
      : this.client.get<T>(url);
  }

  private benchmarkRequestConfig(signal?: AbortSignal) {
    return {
      ...(signal ? { signal } : {}),
      ...(ApiService.benchmarkAccessToken
        ? {
            headers: {
              Authorization: `Bearer ${ApiService.benchmarkAccessToken}`,
            },
          }
        : {}),
    };
  }

  private benchmarkPost<T>(url: string, data?: unknown, signal?: AbortSignal) {
    const requestConfig = this.benchmarkRequestConfig(signal);
    return Object.keys(requestConfig).length > 0
      ? this.client.post<T>(url, data, requestConfig)
      : this.client.post<T>(url, data);
  }

  private benchmarkGet<T>(url: string, signal?: AbortSignal) {
    const requestConfig = this.benchmarkRequestConfig(signal);
    return Object.keys(requestConfig).length > 0
      ? this.client.get<T>(url, requestConfig)
      : this.client.get<T>(url);
  }

  /**
   * Start a new interview session
   */
  async startInterview(
    request: StartInterviewRequest,
  ): Promise<StartInterviewResponse> {
    try {
      const isBenchmarkStart = Boolean(request.metadata?.localModelBenchmark);
      const response = isBenchmarkStart
        ? await this.benchmarkPost<StartInterviewResponse>(
            "/interview/start",
            request,
          )
        : await this.post<StartInterviewResponse>("/interview/start", request);
      this.setSessionToken(response.data.sessionToken);
      return response.data;
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  /**
   * Send a message in an active interview session
   */
  async sendMessage(
    request: SendMessageRequest,
    signal?: AbortSignal,
  ): Promise<SendMessageResponse> {
    try {
      const response = await this.post<SendMessageResponse>(
        `/interview/${request.sessionId}/message`,
        {
          ...(request.messageId ? { messageId: request.messageId } : {}),
          ...(request.benchmarkRetryId
            ? { benchmarkRetryId: request.benchmarkRetryId }
            : {}),
          ...(request.content ? { content: request.content } : {}),
          ...(request.selectedOptionIds
            ? { selectedOptionIds: request.selectedOptionIds }
            : {}),
          // SRN-715: echo per-turn metadata so the persisted user-turn row
          // is self-describing for the processing worker.
          ...(request.interviewQuestionId
            ? { interviewQuestionId: request.interviewQuestionId }
            : {}),
          ...(request.questionType
            ? { questionType: request.questionType }
            : {}),
          ...(request.responseFormat
            ? { responseFormat: request.responseFormat }
            : {}),
          // AS-1844: tag voice answers so ism.audio_s3_key gets populated
          // for downstream playback surfaces (PDF competency PLAY RECORDING,
          // TalentHub side panel).
          ...(request.audioS3Key ? { audioS3Key: request.audioS3Key } : {}),
        },
        signal,
      );
      return response.data;
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  async generateBenchmarkCandidateResponse(
    request: GenerateBenchmarkCandidateResponseRequest,
    signal?: AbortSignal,
  ): Promise<GenerateBenchmarkCandidateResponseResponse> {
    try {
      const response =
        await this.benchmarkPost<GenerateBenchmarkCandidateResponseResponse>(
          "/interview/benchmark/candidate-response",
          request,
          signal,
        );
      return response.data;
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  async getBenchmarkOpenRouterUsage(): Promise<BenchmarkOpenRouterUsage> {
    try {
      const response = await this.benchmarkGet<BenchmarkOpenRouterUsage>(
        "/interview/benchmark/openrouter-usage",
      );
      return response.data;
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  async saveBenchmarkEvaluation(
    request: SaveBenchmarkEvaluationRequest,
    signal?: AbortSignal,
  ): Promise<SaveBenchmarkEvaluationResponse> {
    try {
      const response =
        await this.benchmarkPost<SaveBenchmarkEvaluationResponse>(
          "/interview/benchmark/evaluations",
          request,
          signal,
        );
      return response.data;
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  async getBenchmarkResponseAnnotations(): Promise<GetBenchmarkResponseAnnotationsResponse> {
    try {
      const response =
        await this.benchmarkGet<GetBenchmarkResponseAnnotationsResponse>(
          "/interview/benchmark/annotations",
        );
      return response.data;
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  async saveBenchmarkResponseAnnotation(
    request: SaveBenchmarkResponseAnnotationRequest,
    signal?: AbortSignal,
  ): Promise<BenchmarkResponseAnnotation> {
    try {
      const response = await this.benchmarkPost<BenchmarkResponseAnnotation>(
        "/interview/benchmark/annotations",
        request,
        signal,
      );
      return response.data;
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  async cancelBenchmarkSessions(
    request: CancelBenchmarkSessionsRequest,
  ): Promise<CancelBenchmarkSessionsResponse> {
    try {
      const response =
        await this.benchmarkPost<CancelBenchmarkSessionsResponse>(
          "/interview/benchmark/cancel",
          request,
        );
      return response.data;
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  /** SRN-715 message body shape, shared by sendMessage and the edit PATCH. */
  private buildMessageBody(request: SendMessageRequest) {
    return {
      ...(request.content ? { content: request.content } : {}),
      ...(request.selectedOptionIds
        ? { selectedOptionIds: request.selectedOptionIds }
        : {}),
      ...(request.interviewQuestionId
        ? { interviewQuestionId: request.interviewQuestionId }
        : {}),
      ...(request.questionType ? { questionType: request.questionType } : {}),
      ...(request.responseFormat
        ? { responseFormat: request.responseFormat }
        : {}),
      ...(request.audioS3Key ? { audioS3Key: request.audioS3Key } : {}),
    };
  }

  async getPreviousQuestion(
    sessionId: string,
  ): Promise<PreviousQuestionResponse> {
    try {
      const response = await this.client.post<PreviousQuestionResponse>(
        `/interview/${sessionId}/previous-question`,
      );
      return response.data;
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  async editPreviousResponse(
    request: EditPreviousResponseRequest,
  ): Promise<SendMessageResponse> {
    try {
      const response = await this.client.patch<SendMessageResponse>(
        `/interview/${request.sessionId}/message/${request.messageId}/edit`,
        this.buildMessageBody(request),
      );
      return response.data;
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  /**
   * Get interview session details
   */
  async getSession(
    sessionId: string,
    signal?: AbortSignal,
  ): Promise<InterviewSession> {
    try {
      const response = await this.get<InterviewSession>(
        `/interview/${sessionId}`,
        signal,
      );
      return response.data;
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  /**
   * End an interview session
   */
  async endInterview(sessionId: string): Promise<EndInterviewResponse> {
    try {
      const response = await this.client.post<EndInterviewResponse>(
        `/interview/${sessionId}/end`,
      );
      return response.data;
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  /**
   * Get interview configuration
   */
  async getInterviewConfig(interviewId: string): Promise<InterviewConfig> {
    try {
      const response = await this.client.get<InterviewConfig>(
        `/interview/config/${interviewId}`,
      );
      return response.data;
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  /**
   * Get interview location details
   */
  async getLocation(locationId: string): Promise<InterviewLocation> {
    try {
      const response = await this.client.get<InterviewLocation>(
        `/interview/location/${locationId}`,
      );
      return response.data;
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  /**
   * Get resume session details by token
   */
  async getResumeSession(resumeToken: string): Promise<ResumeSessionResponse> {
    try {
      const response = await this.client.get<ResumeSessionResponse>(
        `/interview/resume/${resumeToken}`,
      );
      return response.data;
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  /**
   * Continue a resume session (pick up where left off)
   */
  async continueResumeSession(token: string): Promise<ContinueResumeResponse> {
    try {
      const response = await this.client.post<ContinueResumeResponse>(
        "/interview/resume/continue",
        { token },
      );
      this.setSessionToken(response.data.sessionToken);
      return response.data;
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  /**
   * Restart a resume session (start over)
   */
  async restartResumeSession(token: string): Promise<RestartResumeResponse> {
    try {
      const response = await this.client.post<RestartResumeResponse>(
        "/interview/resume/restart",
        { token },
      );
      this.setSessionToken(response.data.sessionToken);
      return response.data;
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  /**
   * Save interview progress and send resume link
   */
  async saveProgress(
    sessionId: string,
    candidateEmail: string,
  ): Promise<SaveProgressResponse> {
    try {
      const response = await this.client.post<SaveProgressResponse>(
        `/interview/${sessionId}/save-progress`,
        { candidateEmail },
      );
      return response.data;
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  /**
   * Get all messages for a session (used for resume flow)
   */
  async getSessionMessages(
    sessionId: string,
    signal?: AbortSignal,
  ): Promise<SessionMessage[]> {
    try {
      const response = await this.get<SessionMessage[]>(
        `/interview/${sessionId}/messages`,
        signal,
      );
      return response.data;
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  async generateFileUploadUrl(
    sessionId: string,
    questionId: string,
    fileName: string,
    fileExtension: string,
    contentType: string,
  ): Promise<FileUploadUrlResponse> {
    try {
      const response = await this.client.post<FileUploadUrlResponse>(
        `/interview/${sessionId}/file/upload-url`,
        {
          questionId,
          fileName,
          fileExtension,
          contentType,
        },
      );
      return response.data;
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  async uploadFileResponse(
    uploadUrl: string,
    fields: Record<string, string>,
    file: File,
  ): Promise<void> {
    const formData = new FormData();
    Object.entries(fields).forEach(([key, value]) => {
      formData.append(key, value);
    });
    formData.append("file", file);

    try {
      await axios.post(uploadUrl, formData);
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  /**
   * Translate static UI text using the API translation cache.
   */
  async translateTexts(
    texts: string[],
    targetLanguageCode: string,
  ): Promise<string[]> {
    try {
      const response = await this.client.post<{
        translatedTexts: string[];
        sourceLanguageCode: string;
        targetLanguageCode: string;
      }>("/translation/static", {
        texts,
        targetLanguageCode,
      });
      return response.data.translatedTexts;
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  /**
   * Upload a voice recording to S3 via a presigned PUT URL.
   *
   * The orchestrator presigns the URL for `audio/webm` with a `.webm` key,
   * so the Content-Type sent here MUST match — otherwise the PUT signature
   * fails (or, worse, succeeds and stores non-webm bytes under a `.webm`
   * extension). `useMicrophone` enforces webm/opus on the recording side.
   */
  async uploadVoiceResponse(uploadUrl: string, blob: Blob): Promise<void> {
    const VOICE_UPLOAD_TIMEOUT_MS = 60_000;
    try {
      await axios.put(uploadUrl, blob, {
        headers: { "Content-Type": "audio/webm" },
        timeout: VOICE_UPLOAD_TIMEOUT_MS,
      });
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  /**
   * AS-1816: exchange a stored s3:// audio key for a short-lived presigned
   * HTTPS URL the browser can drop straight into an `<audio>` element. The
   * server validates the key path against the session id, so a stolen
   * bearer token can't enumerate other candidates' recordings.
   */
  async getVoiceResponsePlaybackUrl(
    sessionId: string,
    audioKey: string,
  ): Promise<{ playbackUrl: string; expiresAt: string }> {
    try {
      const response = await this.client.post<{
        playbackUrl: string;
        expiresAt: string;
      }>(`/interview/${sessionId}/audio/playback-url`, { audioKey });
      return {
        playbackUrl: response.data.playbackUrl,
        expiresAt: response.data.expiresAt,
      };
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  private handleError(error: unknown): void {
    if (error instanceof AxiosError) {
      console.error("API Error:", {
        status: error.response?.status,
        message: error.message,
        data: error.response?.data,
      });
    } else {
      console.error("Unknown error:", error);
    }
  }
}

export const apiService = new ApiService();
